import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, getToken, setToken } from '../api/client.js'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const urlToken = params.get('token')
      if (urlToken) {
        setToken(urlToken)
        params.delete('token')
        const newSearch = params.toString() ? `?${params.toString()}` : ''
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + newSearch + window.location.hash,
        )
      }
    } catch {}

    if (!getToken()) {
      setLoading(false)
      return
    }
    api
      .me()
      .then(({ user }) => setUser(user))
      .catch(() => setToken(null))
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (credentials) => {
    const { token, user } = await api.login(credentials)
    setToken(token)
    setUser(user)
    return user
  }, [])

  const register = useCallback(async (data) => {
    const { token, user } = await api.register(data)
    setToken(token)
    setUser(user)
    return user
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
  }, [])

  /**
   * Локально обновляет баланс после операции. Все ручки, которые двигают
   * деньги, возвращают итоговый баланс — берём его вместо лишнего запроса.
   */
  const setBalance = useCallback((balance) => {
    if (typeof balance !== 'number') return
    setUser((current) => (current ? { ...current, balance } : current))
  }, [])

  const refresh = useCallback(async () => {
    if (!getToken()) return null
    const { user } = await api.me()
    setUser(user)
    return user
  }, [])

  const value = useMemo(
    () => ({ user, loading, login, register, logout, setBalance, refresh }),
    [user, loading, login, register, logout, setBalance, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth должен вызываться внутри AuthProvider')
  return context
}
